<template>
  <div class="flex flex-col justify-center gap-10">
    <div class="flex flex-col justify-between lg:flex-row gap-4">
      <titlePrimary :title="isEdit ? $t('supplyLight.editTitle') : $t('supplyLight.newTitle')" />
      <titlePrimary
        class="px-20 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]"
        bgColor="bg-[#00000]"
        textColor="text-[#7CADB9]"
        :title="$t('supplyLight.subtitle')"
      />
    </div>

    <!-- <h2 class="text-[var(--c-primary)]">{{ $t('supplyLight.sectionTitle') }}</h2> -->

    <div class=" grid lg:grid-cols-3 md:grid-cols-2 grid-col-1 gap-10">
      <div class="relative">
        <inputPrimary v-model="searchText"
          :label="$t('supplyLight.fields.searchProperty')"
          :placeholder="$t('supplyLight.fields.searchPlaceholder')" />

        <div v-if="searchLoading" class="absolute left-0 right-0 z-50 mt-1 border rounded p-2 bg-white shadow-lg max-h-60 overflow-auto">
          <p class="text-gray-500">{{ $t('common.loading') }}</p>
        </div>

        <div v-else-if="searchResults && searchResults.data && searchResults.data.length > 0"
          class="absolute left-0 right-0 z-50 mt-1 border rounded p-2 bg-white shadow-lg max-h-60 overflow-auto">
          <ul>
            <li v-for="item in searchResults.data" :key="item.id"
              class="hover:bg-gray-100 cursor-pointer p-2"
              @click="selectSearchResult(item)">
              <p class="truncate">{{ item.name }} · {{ item.address }}</p>
            </li>
          </ul>
        </div>

        <div v-else-if="searchText && searchResults && !searchResults.data.length" class="absolute left-0 right-0 z-50 mt-1 border rounded p-2 bg-white shadow-lg">
          <p>{{ $t('supplyLight.fields.noResults') }}</p>
        </div>

        <span v-if="validationErrors.propertyName" class="text-red-500 text-xs">{{ validationErrors.propertyName }}</span>
      </div>

      <div>
        <inputPrimary v-model="formData.cups"
          :label="$t('supplyLight.fields.cups')"
          :placeholder="$t('supplyLight.fields.cupsPlaceholder')" />
        <span v-if="validationErrors.cups" class="text-red-500 text-xs">{{ validationErrors.cups }}</span>
      </div>

      <div>
        <inputPrimary v-model="formData.address"
          :label="$t('supplyLight.fields.address')"
          :placeholder="$t('supplyLight.fields.addressPlaceholder')" />
        <span v-if="validationErrors.address" class="text-red-500 text-xs">{{ validationErrors.address }}</span>
      </div>

      <div style="margin: 8px 0;">
        <label class="block text-sm font-medium text-gray-700 mb-1">{{$t('supplyLight.fields.company')}}</label>
        <div class="relative" ref="companyDropdownRef">
          <div
            class="flex items-center border rounded-lg px-3 h-11 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] cursor-pointer"
            :class="companyLoading ? 'opacity-60 cursor-not-allowed' : ''"
            @click="toggleCompanyDropdown"
          >
            <div class="w-full text-left text-sm truncate" style="color: var(--c-primary3)">
              {{ selectedCompany || $t('supplyLight.fields.companyPlaceholder') }}
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" style="color: var(--c-primary2)">
              <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
            </svg>
          </div>

          <div v-if="companyDropdownOpen" class="absolute left-0 right-0 z-50 mt-1 border rounded p-2 bg-white shadow-lg max-h-60 overflow-auto">
            <ul>
              <li v-if="companyLoading" class="p-2 text-gray-500">{{ $t('common.loading') }}</li>
              <li v-else-if="companies.length === 0" class="p-2 text-gray-500">{{ $t('supplyLight.fields.noResults') }}</li>
              <li
                v-for="company in companies"
                :key="company.id"
                class="p-2 hover:bg-gray-100 cursor-pointer truncate"
                @click.stop="selectCompany(company)"
                style="color: var(--c-primary3)"
              >
                {{ company.name }}
              </li>
            </ul>
          </div>
        </div>
        <span v-if="companyLoading" class="text-xs text-gray-400">{{$t('common.loading')}}</span>
        <span v-if="validationErrors.company" class="text-red-500 text-xs">{{ validationErrors.company }}</span>
      </div>

      <div>
        <inputPrimary v-model="formData.contract_expiration"
          :label="$t('supplyLight.fields.contractExpiration')"
          type="date"
          :placeholder="$t('supplyLight.fields.contractExpirationPlaceholder')" />
        <span v-if="validationErrors.contract_expiration" class="text-red-500 text-xs">{{ validationErrors.contract_expiration }}</span>
      </div>

      <div>
        <inputPrimary v-model="formData.cif"
          :label="$t('supplyLight.details.labels.cif')"
          :placeholder="$t('supplyLight.details.placeholders.cif')" />
      </div>

      <div>
        <inputPrimary v-model="formData.iban"
          :label="$t('supplyLight.details.labels.iban')"
          :placeholder="$t('supplyLight.details.placeholders.iban')" />
      </div>

      <div>
        <inputPrimary v-model="formData.observations"
          :label="$t('supplyLight.fields.observations')"
          :placeholder="$t('supplyLight.fields.observationsPlaceholder')" />
        <span v-if="validationErrors.observations" class="text-red-500 text-xs">{{ validationErrors.observations }}</span>
      </div>

      <div>
        <inputPrimary v-model="formData.ownerId" :disabled="true"
          :label="$t('supplyLight.fields.ownerId')"
          :placeholder="$t('supplyLight.fields.ownerIdPlaceholder')" 
          />
        <span v-if="validationErrors.ownerId" class="text-red-500 text-xs">{{ validationErrors.ownerId }}</span>
      </div>

      <div>
        <label class="block text-base mb-1 py-1 text-[#385660] truncate cursor-help" title="Liquidar Luz">Liquidar Luz:</label>
        <div class="flex items-center border border-[#3A5762] rounded-lg px-2 h-11 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
          <input 
            type="checkbox" 
            id="liquidate_light" 
            v-model="formData.liquidate_light"
            class="w-5 h-5 text-[#385660] bg-gray-100 border-gray-300 rounded focus:ring-[#385660] focus:ring-2 mr-3 flex-shrink-0"
          />
          <label for="liquidate_light" class="text-sm text-[#385660] px-1 cursor-pointer select-none">
            Marcar para liquidar
          </label>
        </div>
        
      </div>
      <div>
        <inputPrimary v-model="formData.rate"
          :label="$t('supplyLight.fields.rate')"
          :placeholder="$t('supplyLight.fields.ratePlaceholder')" />
        <span v-if="validationErrors.rate" class="text-red-500 text-xs">{{ validationErrors.rate }}</span>
      </div>


      <div class="lg:col-span-3 md:col-span-2 col-span-1">
        <label class="block text-base mb-1 py-1 text-[#385660] truncate cursor-help" :title="$t('supplyLight.fields.ratePeriods')">
          {{ $t('supplyLight.fields.ratePeriods') }}
        </label>
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <label class="block text-xs text-[#385660] mb-1">P1</label>
            <input v-model="formData.p1" type="text" placeholder="0"
              class="w-full h-11 border border-[var(--vt-c-indigo)] rounded-lg px-2 text-sm text-[#7CADB9] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]" />
            <span v-if="validationErrors.p1" class="text-red-500 text-xs">{{ validationErrors.p1 }}</span>
          </div>
          <div>
            <label class="block text-xs text-[#385660] mb-1">P2</label>
            <input v-model="formData.p2" type="text" step="any" min="0" placeholder="0"
              class="w-full h-11 border border-[var(--vt-c-indigo)] rounded-lg px-2 text-sm text-[#7CADB9] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]" />
            <span v-if="validationErrors.p2" class="text-red-500 text-xs">{{ validationErrors.p2 }}</span>
          </div>
          <div>
            <label class="block text-xs text-[#385660] mb-1">P3</label>
            <input v-model="formData.p3" type="text" step="any" min="0" placeholder="0"
              class="w-full h-11 border border-[var(--vt-c-indigo)] rounded-lg px-2 text-sm text-[#7CADB9] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]" />
            <span v-if="validationErrors.p3" class="text-red-500 text-xs">{{ validationErrors.p3 }}</span>
          </div>
          <div>
            <label class="block text-xs text-[#385660] mb-1">P4</label>
            <input v-model="formData.p4" type="text" step="any" min="0" placeholder="0"
              class="w-full h-11 border border-[var(--vt-c-indigo)] rounded-lg px-2 text-sm text-[#7CADB9] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]" />
            <span v-if="validationErrors.p4" class="text-red-500 text-xs">{{ validationErrors.p4 }}</span>
          </div>
          <div>
            <label class="block text-xs text-[#385660] mb-1">P5</label>
            <input v-model="formData.p5" type="text" step="any" min="0" placeholder="0"
              class="w-full h-11 border border-[var(--vt-c-indigo)] rounded-lg px-2 text-sm text-[#7CADB9] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]" />
            <span v-if="validationErrors.p5" class="text-red-500 text-xs">{{ validationErrors.p5 }}</span>
          </div>
          <div>
            <label class="block text-xs text-[#385660] mb-1">P6</label>
            <input v-model="formData.p6" type="text" placeholder="0"
              class="w-full h-11 border border-[var(--vt-c-indigo)] rounded-lg px-2 text-sm text-[#7CADB9] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]" />
            <span v-if="validationErrors.p6" class="text-red-500 text-xs">{{ validationErrors.p6 }}</span>
          </div>
        </div>
      </div>

    </div>

    <div class="flex justify-end">
      <div class=" lg:w-1/3 w-full p-4 flex items-center justify-center">
        <ButtonWhite
          @click="submitForm"
          :disabled="submitting"
          :class="[submitting ? 'opacity-50 cursor-not-allowed w-full' : 'w-full']"
          bgColor="bg-[#25B0D7]"
          textColor="text-white"
          hoverBgColor="bg-[#2AB4D9]"
        >
          {{ isEdit ? $t('supplyLight.buttons.saveChanges') : $t('supplyLight.buttons.save') }}
        </ButtonWhite>
      </div>
    </div>
  </div>
</template>

<script setup>
import { isValidContractCups, normalizeContractCups } from '@/services/contractCups';
import titlePrimary from '@/components/title/title-primary.vue';
import ButtonWhite from '@/components/button/ButtonWhite.vue';
import inputPrimary from '@/components/inputs/input-primary.vue';
import { ref, watch, onMounted, onBeforeUnmount, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios'
import { useRoute, useRouter } from 'vue-router';
import { toast } from 'vue3-toastify';



// Estado compartido
const selectedOption = ref(null)
// const errors = ref([])


const route = useRoute();
const router = useRouter();
const id = route.params.id;

const isEdit = ref(!!id);

const loading = ref(false);
const searchLoading = ref(false); // Indicador de carga para la búsqueda
const error = ref(null);

const submitting = ref(false);

const validationErrors = reactive({});

const formData = ref({
  type: 'light',
  status: 'created',
  cups: '',
  address: '',
  company: '',
  cif: '',
  iban: '',
  rate: '',
  p1: '',
  p2: '',
  p3: '',
  p4: '',
  p5: '',
  p6: '',
  contract_expiration: '',
  observations: '',
  propertyName: '',
  propertyId: '', // ID de la propiedad seleccionada
  ownerId: '',
  ownerName: '',
  liquidate_light: false
})

// Variables para la búsqueda
const searchText = ref('');
const searchResults = ref(null);

// Companies dropdown
const companies = ref([]);
const companyLoading = ref(false);
// Custom company dropdown state (like addGasView)
const companyDropdownOpen = ref(false);
const companyDropdownRef = ref(null);
const selectedCompany = ref(formData.value.company || '');
const { t } = useI18n();

const toggleCompanyDropdown = () => {
  if (companyLoading.value) return;
  companyDropdownOpen.value = !companyDropdownOpen.value;
};

const selectCompany = (company) => {
  formData.value.company = company.name;
  selectedCompany.value = company.name;
  companyDropdownOpen.value = false;
};

const closeCompanyDropdown = () => {
  companyDropdownOpen.value = false;
};

const handleDocumentClick = (e) => {
  if (companyDropdownRef.value && !companyDropdownRef.value.contains(e.target)) {
    closeCompanyDropdown();
  }
};

watch(() => formData.value.company, (val) => {
  selectedCompany.value = val || '';
});

watch(() => formData.value.type, (newValue) => {
  selectedOption.value = newValue;
});

// Método para actualizar la opción seleccionada
// const updateSelectedOption = (value) => {
//   selectedOption.value = value
// }

// const handleFileUpload = (file) => {
//   formData.value.files.push({
//     url: file.url,
//     name: file.name
//   })
//   errors.value = errors.value.filter(e => e !== 'Archivo es obligatorio');
// }

function validateForm() {
  Object.keys(validationErrors).forEach(k => delete validationErrors[k]);

  const setReq = (field, keyFallback) => {
    validationErrors[field] = (t(keyFallback) && t(keyFallback) !== keyFallback) ? t(keyFallback) : t('form.errors.required');
  };

  if (!isValidContractCups(formData.value.cups)) validationErrors.cups = t('liquidation.invalidCups');

  // address: required|string|max:255
  if (!formData.value.address || String(formData.value.address).trim() === '') {
    setReq('address', 'form.validation.address_required');
  } else if (formData.value.address.length > 255) {
    validationErrors.address = t('form.validation.address_max');
  }

  // company: required|string|max:255
  if (!formData.value.company || String(formData.value.company).trim() === '') {
    setReq('company', 'form.validation.company_required');
  } else if (String(formData.value.company).length > 255) {
    validationErrors.company = t('form.validation.company_max');
  }

  // rate: required|string|max:100
  if (!formData.value.rate || String(formData.value.rate).trim() === '') {
    setReq('rate', 'form.validation.rate_required');
  } else if (String(formData.value.rate).length > 100) {
    validationErrors.rate = t('form.validation.rate_max');
  }

  // p1..p6: nullable|numeric
  {
    const checkNumeric = (fieldKey, label) => {
      const raw = formData.value[fieldKey];
      if (raw !== null && raw !== undefined && raw !== '') {
        const str = String(raw).trim();
        if (str !== '') {
          const normalized = str.replace(',', '.');
          if (isNaN(Number(normalized))) {
            validationErrors[fieldKey] = t('form.validation.numeric', { field: label })
          }
        }
      }
    };

    checkNumeric('p1', 'P1');
    checkNumeric('p2', 'P2');
    checkNumeric('p3', 'P3');
    checkNumeric('p4', 'P4');
    checkNumeric('p5', 'P5');
    checkNumeric('p6', 'P6');
  }

  // observations: nullable|string|max:1000
  if (formData.value.observations && formData.value.observations.length > 1000) {
    validationErrors.observations = t('form.validation.comments_max');
  }

  // contract_expiration: required|date|after_or_equal:today
  if (!formData.value.contract_expiration) {
    setReq('contract_expiration', 'form.validation.contract_expiration_required');
  } else {
    const inputDate = new Date(formData.value.contract_expiration);
    if (isNaN(inputDate.getTime())) {
      validationErrors.contract_expiration = 'Fecha no válida.';
    } else {
      const today = new Date();
      today.setHours(0,0,0,0);
      inputDate.setHours(0,0,0,0);
      if (inputDate < today) {
        validationErrors.contract_expiration = t('form.validation.contract_expiration_after_or_equal');
      }
    }
  }

  // type: required|in:gas,light
    if (!formData.value.type || (formData.value.type !== 'gas' && formData.value.type !== 'light')) {
    validationErrors.type = t('form.validation.type_invalid');
  }

  // property and owner checks - ensure messages show under inputs
  if (!formData.value.propertyName || !formData.value.propertyId) {
    validationErrors.propertyName = t('form.errors.required');
  }


  // includes_water: nullable boolean -> no frontend validation needed

  return Object.keys(validationErrors).length === 0;
}

// Nueva función de búsqueda
const performSearch = async (query) => {
  searchLoading.value = true;
  try {
    const endpoint = '/supply/properties/search';
    
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}${endpoint}`,
      {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          query: query,
          per_page: 100,
        }
      }
    );
    searchResults.value = response.data.value; // Asigna los resultados a searchResults
  } catch (error) {
    console.error('Error en la búsqueda:', error);
    searchResults.value = null; // Limpia los resultados en caso de error
  } finally {
    searchLoading.value = false;
  }
};

// Watch para ejecutar la búsqueda cada vez que searchText cambie con debounce
let searchTimeout;
watch(searchText, (newSearchText) => {
  if (!isEdit.value && newSearchText !== formData.value.propertyName) formData.value.propertyId = '';
  // Limpiar el timeout anterior
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }
  
  if (newSearchText.length > 0) {
    // Esperar 300ms antes de ejecutar la búsqueda
    searchTimeout = setTimeout(() => {
      performSearch(newSearchText);
    }, 300);
  } else {
    searchResults.value = null; // Limpia los resultados si el input está vacío
  }
});

// Función para seleccionar un resultado de la búsqueda
const selectSearchResult = (result) => {
  formData.value.propertyName = result.name || result.address; // Asigna el nombre de la Comunidades al formulario
  formData.value.propertyId = result.id;
  formData.value.ownerId = result.propietario?.name || ''; // Asigna el ID de la propiedad
  searchText.value = result.name || result.address; // Actualiza el input de búsqueda con el nombre seleccionado
  searchResults.value = null; // Limpia los resultados de la búsqueda
};

const submitForm = async () => {
  if (submitting.value) return;

  if (!validateForm()) {
    error.value = 'Por favor, revisa los campos marcados en rojo.';
    return;
  }
  submitting.value = true;
  try {
    // Preparar los datos para enviar
    const dataToSend = {
      cups: normalizeContractCups(formData.value.cups),
      address: formData.value.address,
      company: formData.value.company,
      cif: formData.value.cif || null,
      iban: formData.value.iban || null,
      rate: formData.value.rate,
      p1: formData.value.p1,
      p2: formData.value.p2,
      p3: formData.value.p3,
      p4: formData.value.p4,
      p5: formData.value.p5,
      p6: formData.value.p6,
      observations: formData.value.observations || null,
      contract_expiration: new Date(formData.value.contract_expiration).toISOString().slice(0, 19).replace('T', ' '),
      type: formData.value.type,
      liquidate: formData.value.liquidate_light ? 'liquidate' : ''
    };

    let response;
    if (isEdit.value) {
      // Actualizar suministro existente
      response = await axios.put(
        `${import.meta.env.VITE_API_URL}/supply/update/${id}`,
        dataToSend,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
        }
      );
      toast.success('Suministro actualizado correctamente');
    } else {
      // Crear nuevo suministro
      const targetPropertyId = formData.value.propertyId;
      response = await axios.post(
        `${import.meta.env.VITE_API_URL}/supply/create/${targetPropertyId}`,
        dataToSend,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
        }
      );
      toast.success('supplyLight.success.created');
    }

    if (response.status === 200 || response.status === 201) {
      setTimeout(() => {
        router.push(`/light-detail/${response.data.value.id}`);
      }, 1500);
    } else {
      toast.error('supplyLight.error.saving');
    }
  } catch (err) {
    console.error('Error al guardar el suministro:', err);
    error.value = 'supplyLight.error.saving';
    toast.error('supplyLight.error.saving');
  } finally {
    submitting.value = false;
  }
};

// Función para cargar los datos del suministro
const fetchSupplyData = async () => {
  if (!id) return;
  loading.value = true;
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/supply/get/${id}`,
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    if (response.data?.value) {
      const supplyData = response.data.value;
      // Convertir la fecha al formato YYYY-MM-DD para el input date
      const contractDate = new Date(supplyData.contract_expiration);
      const formattedDate = contractDate.toISOString().split('T')[0];

      formData.value = {
        type: supplyData.type,
        status: supplyData.status,
        cups: supplyData.cups,
        address: supplyData.address,
        company: supplyData.company,
        cif: supplyData.cif || '',
        iban: supplyData.iban || '',
        rate: supplyData.rate,
        p1: supplyData.p1 ?? supplyData.P1 ?? '',
        p2: supplyData.p2 ?? supplyData.P2 ?? '',
        p3: supplyData.p3 ?? supplyData.P3 ?? '',
        p4: supplyData.p4 ?? supplyData.P4 ?? '',
        p5: supplyData.p5 ?? supplyData.P5 ?? '',
        p6: supplyData.p6 ?? supplyData.P6 ?? '',
        contract_expiration: formattedDate,
        observations: supplyData.observations,
        propertyName: supplyData.property?.name || '',
        propertyId: supplyData.property?.id || '',
        ownerId: supplyData.property?.propietario?.name || '',
        liquidate_light: supplyData.liquidate_light === 'liquidate' || false
      };
      searchText.value = supplyData.property?.name || '';
    }
  } catch (err) {
    console.error('Error al cargar los datos del suministro:', err);
    error.value = 'supplyLight.error.loading';
  } finally {
    loading.value = false;
  }
};

// Companies dropdown
const fetchCompanies = async () => {
  companyLoading.value = true;
  try {
    const response = await axios.get(`${import.meta.env.VITE_API_URL}/company`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Accept': 'application/json'
      }
    });
    // El resultado es un array directamente, no en response.data.value
    if (Array.isArray(response.data)) {
      companies.value = response.data;
    } else if (response.data?.value && Array.isArray(response.data.value)) {
      companies.value = response.data.value;
    } else {
      companies.value = [];
    }
  } catch (err) {
    console.error('Error al cargar las empresas:', err);
    companies.value = [];
  } finally {
    companyLoading.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleDocumentClick);
  fetchCompanies();
  if (isEdit.value) {
    fetchSupplyData();
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick);
});
</script>

<style scoped>
.grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
}
</style>
